/**
 * SurveyForm — THE ONE answer renderer (docs/SURVEYS.md). The public answer
 * page (`/s/:token`) and the staff "Add response" dialog both render a
 * survey's questions through this component, so a respondent and a staff
 * member typing in a paper form see the same inputs and the same rules.
 *
 * Validation: the resolver is `buildSurveyAnswersSchema(questions)` from
 * `@releve/shared` — the SAME builder the API runs against the stored
 * questions (server-authoritative; this is UX only). `onSubmit` receives the
 * parsed answers (scrubbed text, unanswered keys dropped).
 *
 * Layout (`displayMode`): `all` (the default — the staff dialog passes
 * nothing) renders every question on one page. `one_at_a_time` makes each
 * answerable question a step, with any section heading shown above the first
 * question after it, and "Question N of M" + a progress bar above the step.
 * ONE react-hook-form instance holds every answer, so Back keeps them. The
 * form's submit is STEP-AWARE: before the last step it validates only the
 * current question (`trigger(id)`) and moves on — Next is `type="submit"`, so
 * Enter in an input means Next with no key handling; on the last step it is
 * the normal `handleSubmit` (where the all-blank refusal shows). A failed
 * final submit jumps to the first step holding an error; on the current step
 * focus goes to the input RHF can focus, else to the question's legend. Each step
 * change moves focus to the new question's legend; `resetKey` returns to
 * step 1. Footer (docs/WIZARDS.md): Back on the left, Next / Submit on the
 * right. Double-click guard: a submit within `STEP_SUBMIT_GUARD_MS` of ANY step
 * change is ignored, so the second click of a double-click on Next — landing
 * on the next step's Next, or on the Submit that replaced it — can neither skip
 * a question nor send the survey.
 *
 * SECURITY — PLAIN TEXT ONLY. Every org-authored string (title, labels, help,
 * options, rows, columns, scale end labels) is rendered as a React text node.
 * No `dangerouslySetInnerHTML`, no markdown, no auto-linking, and no
 * `style` / `className` value ever comes from survey data.
 */
import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Controller, useForm, type Control, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import {
  SURVEY_EMAIL_MAX,
  SURVEY_LONG_TEXT_MAX,
  SURVEY_PHONE_MAX,
  SURVEY_SHORT_TEXT_MAX,
  buildSurveyAnswersSchema,
  isAnswerableQuestion,
  type SurveyAnswerableQuestion,
  type SurveyAnswers,
  type SurveyDisplayMode,
  type SurveyQuestion,
} from '@releve/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumberInput } from '@/components/ui/number-input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toggleVariants } from '@/components/ui/toggle';
import { useTranslation } from '@/hooks/useTranslation';
import { formatLocalISODate, parseLocalISODate } from '@/lib/dateUtils';
import { cn } from '@/lib/utils';
import type { SurveyTranslate } from '../surveyLabels';
import {
  emptySurveyFormValues,
  isSurveyFormBlank,
  surveyAnswerErrorMessage,
  surveyEmptyResponseMessage,
  type SurveyFormValues,
} from './surveyAnswerForm';

export type { SurveyFormValues };

/** What client validation refused (`onSubmitBlocked`). Positions are 1-based among answerable questions. */
export interface SurveySubmitBlocked {
  /** Answerable questions holding an error. */
  errorCount: number;
  /** Nothing answered at all. */
  blank: boolean;
  /** The first question holding an error; null when none does (the all-blank refusal). */
  firstInvalidPosition: number | null;
}

export interface SurveyFormProps {
  questions: readonly SurveyQuestion[];
  /** Receives the PARSED answers (the shared schema's output). */
  onSubmit: (answers: SurveyAnswers) => void | Promise<void>;
  /** The `<form id>` — lets a dialog footer button submit it (`form={formId}`). */
  formId?: string;
  /** Render the built-in submit button under the questions. Default true. */
  showSubmit?: boolean;
  submitLabel?: string;
  submittingLabel?: string;
  isSubmitting?: boolean;
  disabled?: boolean;
  /** Rendered inside the form, after the questions (the public page's honeypot). */
  children?: ReactNode;
  /** Bumping this clears the form back to empty (e.g. "send another response") and returns to step 1. */
  resetKey?: number;
  /**
   * Answers to start from (the volunteer intake's re-ask pre-fill), keyed by
   * question id in the stored answer shape; a reset returns to them. Keep the
   * reference stable: a new object resets the form. Omitted → empty.
   */
  initialValues?: SurveyFormValues;
  /** `all` (default): every question on one page; `one_at_a_time`: one question per step. */
  displayMode?: SurveyDisplayMode;
  /**
   * Analytics hooks (the public page only). Positions / steps are 1-based
   * among answerable questions; nothing about the answer itself is passed.
   * Fires once per question, on its first user edit (not on reset).
   */
  onQuestionAnswered?: (position: number) => void;
  /** One-at-a-time: fires once per step on first reaching it going forward; step 1 never fires. */
  onStepReached?: (step: number) => void;
  /** Client validation refused Submit — or, one at a time, Next. */
  onSubmitBlocked?: (blocked: SurveySubmitBlocked) => void;
}

/** A submit this soon after a step change is the tail of a double-click on Next — ignored. */
export const STEP_SUBMIT_GUARD_MS = 400;

/** True when RHF holds an error anywhere under the question's key (grids nest one per row). */
function hasError(errors: FieldErrors<SurveyFormValues>, id: string): boolean {
  return errors[id] !== undefined;
}

export function SurveyForm({
  questions,
  onSubmit,
  formId,
  showSubmit = true,
  submitLabel,
  submittingLabel,
  isSubmitting = false,
  disabled = false,
  children,
  resetKey,
  initialValues,
  displayMode = 'all',
  onQuestionAnswered,
  onStepReached,
  onSubmitBlocked,
}: SurveyFormProps) {
  const { t } = useTranslation('surveys');
  const generatedId = useId();
  const idPrefix = formId ?? `survey-form-${generatedId.replace(/:/g, '')}`;

  const schema = useMemo(() => buildSurveyAnswersSchema(questions), [questions]);
  const defaultValues = useMemo(
    () => (initialValues ? { ...emptySurveyFormValues(questions), ...initialValues } : emptySurveyFormValues(questions)),
    [questions, initialValues],
  );
  const answerable = useMemo(() => questions.filter(isAnswerableQuestion), [questions]);
  /** Each answerable question's section headings — the sections since the previous answerable question. */
  const leadingSections = useMemo(() => {
    const map = new Map<string, SurveyQuestion[]>();
    let pending: SurveyQuestion[] = [];
    for (const q of questions) {
      if (isAnswerableQuestion(q)) {
        map.set(q.id, pending);
        pending = [];
      } else {
        pending.push(q);
      }
    }
    return map;
  }, [questions]);

  const form = useForm<SurveyFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
    shouldFocusError: true,
  });

  const stepped = displayMode === 'one_at_a_time' && answerable.length > 0;
  const [step, setStep] = useState(0);
  const current = Math.min(step, Math.max(answerable.length - 1, 0));
  const isLastStep = !stepped || current >= answerable.length - 1;
  // Bumped on every step navigation; the effect below then focuses the new legend.
  const [navTick, setNavTick] = useState(0);
  const legendRef = useRef<HTMLLegendElement>(null);
  const stepChangedAtRef = useRef(0);
  // Analytics bookkeeping: the furthest step reached, and the questions already reported answered.
  const maxStepRef = useRef(0);
  const answeredRef = useRef(new Set<string>());
  const goToStep = (next: number) => {
    stepChangedAtRef.current = Date.now();
    if (next > maxStepRef.current) {
      maxStepRef.current = next;
      if (next > 0) onStepReached?.(next + 1);
    }
    setStep(next);
    setNavTick((n) => n + 1);
  };
  useEffect(() => {
    if (navTick > 0) legendRef.current?.focus();
  }, [navTick]);

  const firstReset = useRef(true);
  useEffect(() => {
    if (firstReset.current) {
      firstReset.current = false;
      return;
    }
    form.reset(defaultValues);
    setStep(0);
    maxStepRef.current = 0;
    answeredRef.current = new Set();
  }, [resetKey, defaultValues, form]);

  // First user edit of each answerable question. Every input is one
  // `Controller name={q.id}` (grids included), whose onChange emits
  // `{ name, type: 'change' }`; `reset` and `trigger` carry no `type`.
  useEffect(() => {
    if (!onQuestionAnswered) return;
    const sub = form.watch((_, info) => {
      if (info.type !== 'change' || !info.name) return;
      const id = info.name;
      const i = answerable.findIndex((q) => q.id === id);
      if (i >= 0 && !answeredRef.current.has(id)) {
        answeredRef.current.add(id);
        onQuestionAnswered(i + 1);
      }
    });
    return () => sub.unsubscribe();
  }, [form, answerable, onQuestionAnswered]);

  const errors = form.formState.errors;
  const values = form.watch();
  // The schema's all-blank refusal (SURVEY_EMPTY_RESPONSE) is an issue at the
  // ROOT path, which the zod resolver files under the empty key. RHF only
  // re-validates the edited field on change, so the message is also hidden
  // as soon as anything is answered (the next submit re-runs the schema).
  const emptyResponse = errors[''] !== undefined && isSurveyFormBlank(values);

  const submit = form.handleSubmit(
    async (parsed) => {
      await onSubmit(parsed as SurveyAnswers);
    },
    (failed) => {
      const firstInvalid = answerable.findIndex((q) => failed[q.id] !== undefined);
      // isSurveyFormBlank, not failed['']: with a required question an
      // all-blank form refuses per question, never at the root.
      onSubmitBlocked?.({
        errorCount: answerable.filter((q) => failed[q.id] !== undefined).length,
        blank: isSurveyFormBlank(form.getValues()),
        firstInvalidPosition: firstInvalid >= 0 ? firstInvalid + 1 : null,
      });
      if (!stepped) return;
      // Jump to the first step holding an error (an all-blank refusal has none: it shows here).
      if (firstInvalid >= 0 && firstInvalid !== current) {
        goToStep(firstInvalid);
        return;
      }
      // The current step: RHF focuses a text / number / time input itself; a
      // choice, scale, rating, grid, date or phone answer has no ref it can
      // focus, so put focus on the question's legend (whose name carries the
      // error) unless it is already inside the question.
      const legend = legendRef.current;
      if (legend && !legend.parentElement?.contains(document.activeElement)) legend.focus();
    },
  );

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    // Double-click / key-repeat guard on EVERY step change: the second click
    // lands on the re-rendered Next (skipping a question) or on Submit
    // (sending early) — either way it is ignored.
    if (stepped && Date.now() - stepChangedAtRef.current < STEP_SUBMIT_GUARD_MS) {
      event.preventDefault();
      return;
    }
    if (isLastStep) return submit(event);
    // Next: validate only this step's question, then move on.
    event.preventDefault();
    const stepIndex = current;
    void form.trigger(answerable[stepIndex].id, { shouldFocus: true }).then((ok) => {
      if (ok) goToStep(stepIndex + 1);
      else
        onSubmitBlocked?.({
          errorCount: 1,
          blank: isSurveyFormBlank(form.getValues()),
          firstInvalidPosition: stepIndex + 1,
        });
    });
  };

  const renderSection = (q: SurveyQuestion) => (
    <section key={q.id} className="space-y-1 border-b pb-3 pt-2">
      <h3 className="text-lg font-medium">{q.label}</h3>
      {q.help && <p className="whitespace-pre-line text-sm text-muted-foreground">{q.help}</p>}
    </section>
  );

  const renderQuestion = (q: SurveyAnswerableQuestion, questionNumber: number, isStep: boolean) => {
    const fieldId = `${idPrefix}-q${questionNumber}`;
    const invalid = hasError(errors, q.id);
    const message = invalid ? surveyAnswerErrorMessage(q, values[q.id], t) : null;
    return (
      <fieldset
        key={q.id}
        className="space-y-2"
        disabled={disabled || isSubmitting}
      >
        <legend
          ref={isStep ? legendRef : undefined}
          tabIndex={isStep ? -1 : undefined}
          className="flex flex-wrap items-baseline gap-x-2 text-sm font-medium focus:outline-none"
        >
          <span>
            {q.label}
            {q.required && (
              <>
                <span aria-hidden="true" className="text-destructive">
                  {' *'}
                </span>
                <span className="sr-only"> {t('answer.requiredSr')}</span>
              </>
            )}
          </span>
          {/* Inline with the label (docs/COMPONENT_PATTERNS.md form errors). Inside
              the legend it is part of the question's accessible name, so the
              fieldset carries no aria-describedby (it would be read twice). */}
          {message && (
            <span id={`${fieldId}-error`} className="text-xs font-normal text-destructive" role="alert">
              {message}
            </span>
          )}
        </legend>
        {q.help && <p className="whitespace-pre-line text-xs text-muted-foreground">{q.help}</p>}
        <QuestionInput question={q} control={form.control} fieldId={fieldId} invalid={invalid} t={t} />
      </fieldset>
    );
  };

  const busy = disabled || isSubmitting;
  const submitButton = (
    <Button key="submit" type="submit" disabled={busy}>
      {isSubmitting ? (submittingLabel ?? t('public.submitting')) : (submitLabel ?? t('public.submit'))}
    </Button>
  );

  let body: ReactNode;
  if (stepped) {
    const q = answerable[current];
    const total = answerable.length;
    body = (
      <>
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {t('answer.progress', { current: current + 1, total })}
          </p>
          <Progress value={current + 1} max={total} className="h-1.5" aria-hidden="true" />
        </div>
        {(leadingSections.get(q.id) ?? []).map(renderSection)}
        {renderQuestion(q, current + 1, true)}
      </>
    );
  } else {
    let questionNumber = 0;
    body = questions.map((q) => {
      if (!isAnswerableQuestion(q)) return renderSection(q);
      questionNumber += 1;
      return renderQuestion(q, questionNumber, false);
    });
  }

  return (
    <form id={idPrefix} onSubmit={handleFormSubmit} noValidate className="space-y-6">
      {body}

      {emptyResponse && (
        <Alert variant="destructive">
          <AlertDescription>{surveyEmptyResponseMessage(t)}</AlertDescription>
        </Alert>
      )}

      {children}

      {stepped ? (
        // docs/WIZARDS.md footer: Back left, spacer, the forward action right.
        <div className="flex items-center gap-2">
          {current > 0 && (
            <Button type="button" variant="outline" disabled={busy} onClick={() => goToStep(current - 1)}>
              <ChevronLeft className="mr-1 h-4 w-4" aria-hidden />
              {t('answer.back')}
            </Button>
          )}
          <div className="flex-1" />
          {isLastStep ? (
            showSubmit && submitButton
          ) : (
            // A distinct key: React must not reuse this element as the Submit button.
            <Button key="next" type="submit" disabled={busy}>
              {t('answer.next')}
              <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      ) : (
        showSubmit && submitButton
      )}
    </form>
  );
}


/* --------------------------------- inputs -------------------------------- */

interface QuestionInputProps {
  question: SurveyAnswerableQuestion;
  control: Control<SurveyFormValues>;
  fieldId: string;
  invalid: boolean;
  t: SurveyTranslate;
}

const asString = (value: unknown) => (typeof value === 'string' ? value : '');
/** A numeric answer as Radix's string value ('' = unanswered). */
const asNumberValue = (value: unknown) => (typeof value === 'number' ? String(value) : '');
const asStringArray = (value: unknown) =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

function toggle(list: readonly string[], id: string, on: boolean): string[] {
  return on ? (list.includes(id) ? [...list] : [...list, id]) : list.filter((x) => x !== id);
}

function QuestionInput({ question: q, control, fieldId, invalid, t }: QuestionInputProps) {
  const errorClass = invalid ? 'border-destructive' : undefined;
  return (
    <Controller
      control={control}
      name={q.id}
      render={({ field }) => {
        switch (q.type) {
          case 'short_text':
            return (
              <Input
                id={fieldId}
                aria-label={q.label}
                value={asString(field.value)}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                ref={field.ref}
                maxLength={SURVEY_SHORT_TEXT_MAX}
                className={errorClass}
              />
            );
          case 'long_text':
            return (
              <Textarea
                id={fieldId}
                aria-label={q.label}
                value={asString(field.value)}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                ref={field.ref}
                rows={4}
                maxLength={SURVEY_LONG_TEXT_MAX}
                className={errorClass}
              />
            );
          case 'email':
            return (
              <Input
                id={fieldId}
                aria-label={q.label}
                type="email"
                autoComplete="email"
                value={asString(field.value)}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                ref={field.ref}
                maxLength={SURVEY_EMAIL_MAX}
                className={errorClass}
              />
            );
          case 'phone':
            return (
              <PhoneInput
                value={asString(field.value) || undefined}
                onChange={(value) => field.onChange((value ?? '').slice(0, SURVEY_PHONE_MAX))}
                placeholder=""
                hasError={invalid}
              />
            );
          case 'date':
            return (
              <DatePicker
                value={parseLocalISODate(asString(field.value)) ?? undefined}
                onChange={(date) => field.onChange(date ? formatLocalISODate(date) : '')}
                className={errorClass}
              />
            );
          case 'time':
            return (
              <Input
                id={fieldId}
                aria-label={q.label}
                type="time"
                value={asString(field.value)}
                onChange={(e) => field.onChange(e.target.value)}
                onBlur={field.onBlur}
                ref={field.ref}
                className={cn('w-40', errorClass)}
              />
            );
          case 'number':
            return (
              <NumberInput
                id={fieldId}
                aria-label={q.label}
                value={typeof field.value === 'number' ? field.value : ''}
                onChange={(value) => field.onChange(value === '' ? '' : Number(value))}
                onBlur={field.onBlur}
                ref={field.ref}
                allowEmpty
                step={q.integer ? 1 : 'any'}
                min={q.min ?? undefined}
                max={q.max ?? undefined}
                className={cn('w-48', errorClass)}
              />
            );
          case 'single_choice':
            return (
              <RadioGroup
                value={asString(field.value)}
                onValueChange={field.onChange}
                aria-label={q.label}
              >
                {q.options.map((option, i) => (
                  <div key={option.id} className="flex items-start gap-2">
                    <RadioGroupItem value={option.id} id={`${fieldId}-o${i}`} className="mt-0.5" />
                    <Label htmlFor={`${fieldId}-o${i}`} className="cursor-pointer font-normal leading-snug">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            );
          case 'dropdown':
            return (
              <Select value={asString(field.value) || undefined} onValueChange={field.onChange}>
                <SelectTrigger id={fieldId} aria-label={q.label} className={cn('w-full sm:w-80', errorClass)}>
                  <SelectValue placeholder={t('surveys:answer.select')} />
                </SelectTrigger>
                <SelectContent>
                  {q.options.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          case 'multi_choice': {
            const picked = asStringArray(field.value);
            return (
              // The enclosing <fieldset>/<legend> already names this group.
              <div className="grid gap-2">
                {q.options.map((option, i) => (
                  <div key={option.id} className="flex items-start gap-2">
                    <Checkbox
                      id={`${fieldId}-o${i}`}
                      className="mt-0.5"
                      checked={picked.includes(option.id)}
                      onCheckedChange={(checked) => field.onChange(toggle(picked, option.id, checked === true))}
                    />
                    <Label htmlFor={`${fieldId}-o${i}`} className="cursor-pointer font-normal leading-snug">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            );
          }
          case 'scale': {
            const points = Array.from({ length: q.max - q.min + 1 }, (_, i) => q.min + i);
            return (
              // Desktop: min label · buttons · max label on one line. Mobile:
              // the buttons take the full row and the end labels sit below.
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {q.min_label && (
                  <span className="order-2 text-xs text-muted-foreground sm:order-none">{q.min_label}</span>
                )}
                <RadioGroup
                  value={asNumberValue(field.value)}
                  onValueChange={(v) => field.onChange(Number(v))}
                  aria-label={q.label}
                  className="order-1 grid w-full gap-1 sm:order-none sm:flex sm:w-auto sm:flex-wrap sm:justify-start sm:gap-1.5"
                  // The one inline style: the point COUNT (a number), never data text.
                  style={{ gridTemplateColumns: `repeat(${points.length}, minmax(0, 1fr))` }}
                >
                  {points.map((point) => (
                    // The bare Radix item (the app's RadioGroupItem draws a dot indicator)
                    // wearing the outline toggle look: the number button IS the radio.
                    <RadioGroupPrimitive.Item
                      key={point}
                      value={String(point)}
                      id={`${fieldId}-p${point}`}
                      // cn (twMerge) so px-0 REPLACES the size variant's px-3 —
                      // cva's own `className` only appends, leaving both.
                      className={cn(
                        toggleVariants({ variant: 'outline' }),
                        'min-w-0 px-0 tabular-nums data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground sm:min-w-10 sm:px-3',
                      )}
                    >
                      {point}
                    </RadioGroupPrimitive.Item>
                  ))}
                </RadioGroup>
                {q.max_label && (
                  <span className="order-3 ml-auto text-xs text-muted-foreground sm:order-none sm:ml-0">
                    {q.max_label}
                  </span>
                )}
              </div>
            );
          }
          case 'rating': {
            const current = typeof field.value === 'number' ? field.value : 0;
            return (
              <RadioGroup
                value={asNumberValue(field.value)}
                onValueChange={(v) => field.onChange(Number(v))}
                aria-label={q.label}
                className="flex gap-1"
              >
                {Array.from({ length: q.max }, (_, i) => i + 1).map((value) => (
                  <RadioGroupPrimitive.Item
                    key={value}
                    value={String(value)}
                    aria-label={t('surveys:answer.starLabel', { value, max: q.max })}
                    className="rounded p-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Star
                      className={cn(
                        'h-6 w-6',
                        value <= current ? 'fill-primary text-primary' : 'text-muted-foreground',
                      )}
                    />
                  </RadioGroupPrimitive.Item>
                ))}
              </RadioGroup>
            );
          }
          case 'choice_grid':
          case 'checkbox_grid': {
            const cells = asRecord(field.value);
            const setCell = (rowId: string, next: string | string[] | undefined) => {
              const copy = { ...cells };
              if (next === undefined || (Array.isArray(next) && next.length === 0)) delete copy[rowId];
              else copy[rowId] = next;
              field.onChange(copy);
            };
            return (
              <div className="overflow-x-auto">
                <div
                  // w-max + content-sized tracks: the grid hugs its columns
                  // (left-aligned) and scrolls horizontally when it is wider than the card.
                  className="grid w-max items-center justify-start gap-x-4 gap-y-2"
                  // The one inline style: the column COUNT (a number), never data text.
                  style={{
                    gridTemplateColumns: `fit-content(16rem) repeat(${q.columns.length}, minmax(3rem, max-content))`,
                  }}
                >
                  <span />
                  {q.columns.map((column) => (
                    <span key={column.id} className="text-center text-xs text-muted-foreground">
                      {column.label}
                    </span>
                  ))}
                  {q.rows.map((row, r) =>
                    q.type === 'choice_grid' ? (
                      <GridRadioRow
                        key={row.id}
                        rowLabel={row.label}
                        columns={q.columns}
                        value={asString(cells[row.id])}
                        onChange={(colId) => setCell(row.id, colId)}
                        idPrefix={`${fieldId}-r${r}`}
                      />
                    ) : (
                      <GridCheckboxRow
                        key={row.id}
                        rowLabel={row.label}
                        columns={q.columns}
                        value={asStringArray(cells[row.id])}
                        onChange={(ids) => setCell(row.id, ids)}
                        idPrefix={`${fieldId}-r${r}`}
                      />
                    ),
                  )}
                </div>
              </div>
            );
          }
        }
      }}
    />
  );
}

interface GridRowProps<V> {
  rowLabel: string;
  columns: ReadonlyArray<{ id: string; label: string }>;
  value: V;
  onChange: (value: V) => void;
  idPrefix: string;
}

function GridRadioRow({ rowLabel, columns, value, onChange, idPrefix }: GridRowProps<string>) {
  return (
    <RadioGroup value={value} onValueChange={onChange} aria-label={rowLabel} className="contents">
      <span className="text-sm">{rowLabel}</span>
      {columns.map((column, c) => (
        <div key={column.id} className="flex justify-center">
          <RadioGroupItem value={column.id} id={`${idPrefix}-c${c}`} aria-label={`${rowLabel}: ${column.label}`} />
        </div>
      ))}
    </RadioGroup>
  );
}

function GridCheckboxRow({ rowLabel, columns, value, onChange, idPrefix }: GridRowProps<string[]>) {
  return (
    <div role="group" aria-label={rowLabel} className="contents">
      <span className="text-sm">{rowLabel}</span>
      {columns.map((column, c) => (
        <div key={column.id} className="flex justify-center">
          <Checkbox
            id={`${idPrefix}-c${c}`}
            aria-label={`${rowLabel}: ${column.label}`}
            checked={value.includes(column.id)}
            onCheckedChange={(checked) => onChange(toggle(value, column.id, checked === true))}
          />
        </div>
      ))}
    </div>
  );
}
